import PropTypes from "prop-types"
import React from "react"
import MaterialReactTable, {
  MRT_FullScreenToggleButton,
  MRT_ShowHideColumnsButton,
  MRT_ToggleFiltersButton
} from "material-react-table"
import { MRT_Localization_PT_BR } from "material-react-table/locales/pt-BR"
import { Box, ThemeProvider } from "@mui/material"
import { merge } from "lodash"
import MinusIcon from "@mui/icons-material/Remove"
import AddIcon from "@mui/icons-material/Add"
import { baseTableProps, tableTheme, tableTokens } from "./table-theme"

/**
 * Tabela base da aplicação: aplica tema e layout padrão ao MaterialReactTable.
 * É o único ponto do projeto que importa material-react-table diretamente — telas novas
 * devem usar este componente (dados locais) ou list-data-table (busca no servidor).
 *
 * Todos os props recebidos sobrescrevem os padrões. As exceções são initialState e
 * displayColumnDefOptions, que são mesclados: quem chama complementa a base em vez de
 * substituí-la (ex.: definir "mrt-row-expand" sem perder as métricas de "mrt-row-actions").
 *
 * renderToolbarExtraActions({ table }) acrescenta botões à esquerda dos padrões da
 * toolbar, para a tela customizar sem precisar redefinir renderToolbarInternalActions.
 */
const BaseDataTable = ({ initialState, displayColumnDefOptions, renderToolbarExtraActions, ...rest }) => (
  <ThemeProvider theme={ tableTheme }>
    <MaterialReactTable
      { ...baseTableProps }
      localization={ MRT_Localization_PT_BR }
      renderToolbarInternalActions={ ({ table }) => (
        <>
          { renderToolbarExtraActions ? renderToolbarExtraActions({ table }) : null }
          <MRT_ToggleFiltersButton table={ table } />
          <MRT_ShowHideColumnsButton table={ table } />
          <MRT_FullScreenToggleButton table={ table } />
        </>
      ) }
      muiExpandButtonProps={ ({ row, table }) => ({
        onClick: () => table.setExpanded({ [row.id]: !row.getIsExpanded() }),
        children: row.getIsExpanded() ? <MinusIcon></MinusIcon> : <AddIcon></AddIcon>
      }) }
      renderEmptyRowsFallback={ ({ table }) => (
        <Box
          sx={ {
            position: "sticky",
            left: 0,
            width: "100%",
            textAlign: "center",
            padding: "0.5rem",
            color: tableTokens.colorMuted,
            fontSize: tableTokens.fontSizeUi,
            fontFamily: tableTokens.fontFamily
          } }
        >
          { table.options.localization.noRecordsToDisplay }
        </Box>
      ) }
      { ...rest }
      initialState={ merge({}, baseTableProps.initialState, initialState) }
      displayColumnDefOptions={ merge({}, baseTableProps.displayColumnDefOptions, displayColumnDefOptions) }
    />
  </ThemeProvider>
)

BaseDataTable.propTypes = {
  initialState: PropTypes.object,
  displayColumnDefOptions: PropTypes.object,
  renderToolbarExtraActions: PropTypes.func
}

export default BaseDataTable
